import { useTranslations } from 'next-intl';
import MagnifyingGlassLight from '@/assets/magnifyingGlassLight.svg';
import { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import SearchTermInput from '../search/SearchTermInput';
import { useRouter } from 'next/router';
import { UserContext } from '../ContextProviders';
import ReportListCard from '../report/ReportListCard';
import AutocompleteContent from '../search/AutocompleteContent';
import { RecommendationsContext } from '..';

const HeaderSearch = ({ className, dark }: { className?: string; dark?: boolean }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const user = useContext(UserContext);
  const recommendations = useContext(RecommendationsContext);

  const t = useTranslations();
  const router = useRouter();

  const onSubmit = () => {
    setMenuOpen(false);
    const query = {
      searchTerm,
    };
    const url = new URL(window.location.href);
    url.pathname = '/search';
    url.search = new URLSearchParams(query).toString();
    router.push(url);
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [router.asPath]);

  return (
    <div className={className}>
      <div className="flex w-[90px] cursor-text items-center py-2 xl:w-[130px]" onClick={() => setMenuOpen(!menuOpen)}>
        {!menuOpen && (
          <MagnifyingGlassLight className={classNames('pointer-events-none mr-2 h-6 w-6 shrink-0', dark && 'fill-white')} />
        )}
        {!menuOpen && (
          <input
            type="text"
            name="searchTerm"
            className={classNames(
              'pointer-events-none w-full bg-transparent text-base outline-none placeholder:text-transparent',
              dark ? 'xl:placeholder:text-white/40' : 'xl:placeholder:text-gray-400',
            )}
            placeholder={t('header.$searchPlaceholder')}
            disabled
          />
        )}
      </div>

      <div
        className={classNames(
          'fixed inset-x-0 top-[var(--header-height)] z-40 h-[calc(100vh-var(--header-height))] bg-black/30',
          'transition-all duration-200 ease-out',
          menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onMouseEnter={() => setMenuOpen(true)}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />

      <div
        className={classNames(
          'fixed inset-x-0 top-[var(--header-height)] z-50 max-h-[calc(100vh-var(--header-height))] overflow-auto bg-white shadow-md',
          'transition-all duration-200 ease-out will-change-transform',
          menuOpen ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0',
        )}
        onMouseEnter={() => setMenuOpen(true)}
        data-lenis-prevent
      >
        <div className="flex flex-col">
          <div className="bg-gray-2">
            <div className="container flex">
              <div className="flex w-8/12 py-5 pr-4 pl-3">
                <SearchTermInput
                  placeholder={t('search.$searchSomethingPlaceholder')}
                  autofocus={menuOpen}
                  value={searchTerm}
                  onChange={(value) => setSearchTerm(value)}
                  onSubmit={onSubmit}
                  onClear={() => setSearchTerm('')}
                  className="[&_input]:h-8"
                />
              </div>

              <div className="flex w-4/12 items-center pl-16 font-bold">{t('search.$recentlyviewed')}</div>
            </div>
          </div>
          <div className="search-bg" style={{ background: 'linear-gradient(90deg, #fff 50%, #f2f2f2 0)' }}>
            <div className="container flex">
              <div className="w-8/12 bg-white">
                <AutocompleteContent searchTerm={searchTerm} />
              </div>
              <div className="-mt-4 flex w-4/12 flex-col pb-10 pl-16">
                {!user?.isIpBasedIdentity &&
                  recommendations.recentlyViewed.length > 0 &&
                  recommendations.recentlyViewed.map((report) => (
                    <ReportListCard key={report.reportId} data={report} small />
                  ))}

                <div className="flex items-center py-3 font-bold">{t('search.$youmightlike')}</div>
                {recommendations.popularReports.length > 0 &&
                  recommendations.popularReports.map((report) => (
                    <ReportListCard key={report.reportId} data={report} small />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderSearch;
